import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { RequestParams, ApiResponse } from '@/shared/api';
import type { ApiResponseList } from '@/shared/api/types';
import { COMPANY_KEYS } from '../lib';
import { CompanyApi } from '../api';
import type {
  CompanyBase,
  CompanyById,
  CompanyCurrentAmountFillPayload,
  CompanyLimitUpdatePayload,
} from './types';

export const useCompanyList = (params?: RequestParams) => {
  return useQuery<ApiResponseList<CompanyBase>>({
    queryKey: COMPANY_KEYS.list(params),
    queryFn: () => CompanyApi.getList(params),
    placeholderData: keepPreviousData,
  });
};

export const useCompanyById = (id: number) => {
  return useQuery<ApiResponse<CompanyById>>({
    queryKey: COMPANY_KEYS.detail(id),
    queryFn: () => CompanyApi.getById(id),
    enabled: !!id,
  });
};

export const useUpdateCompanyLimit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CompanyLimitUpdatePayload) => CompanyApi.updateLimit(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPANY_KEYS.all });
    },
  });
};

export const useFillCompanyCurrentAmount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CompanyCurrentAmountFillPayload) => CompanyApi.fillCurrentAmount(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPANY_KEYS.all });
    },
  });
};
